import {
  IGNORE_AFTER,
  nextPrompt,
  presentsReminders,
  reminderPrompt,
  type ClientAction,
  type DeviceProfile,
  type GameState,
  type Presence,
  type PromptPlan,
} from "@squire/core";

export interface PromptRunnerDeps {
  state: () => GameState | null;
  deviceId: () => string;
  profile: () => DeviceProfile;
  presence: () => Presence;
  busy: () => boolean;
  now: () => number;
  present: (plan: PromptPlan) => void;
  act: (action: ClientAction) => void;
}

export const TICK_MS = 15_000;
/** After presenting, wait for the server to record it before asking anything else. */
const SETTLE_MS = 60_000;
const MAX_TRACKED = 50;

/**
 * Runs the core scheduler on the device that hosts the companion (SPEC 6.2).
 * Also shows due reminders on a wall device while a pocket companion sleeps (SPEC 6.5).
 */
export class PromptRunner {
  private timer: ReturnType<typeof setInterval> | null = null;
  private ignoreTimer: ReturnType<typeof setTimeout> | null = null;
  private settleUntil = 0;
  private readonly shownReminders = new Map<string, number>();
  lastTickAt = 0;

  constructor(private readonly d: PromptRunnerDeps) {}

  start(): void {
    if (this.timer) return;
    this.timer = setInterval(() => this.tick(), TICK_MS);
    setTimeout(() => this.tick(), 2000);
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    if (this.ignoreTimer) clearTimeout(this.ignoreTimer);
    this.timer = null;
    this.ignoreTimer = null;
  }

  /** The user reacted (spoke or picked a chip): the prompt was not ignored. */
  resolved(): void {
    if (this.ignoreTimer) clearTimeout(this.ignoreTimer);
    this.ignoreTimer = null;
  }

  tick(): PromptPlan | null {
    const now = this.d.now();
    this.lastTickAt = now;
    const s = this.d.state();
    if (!s || !s.onboarded || (typeof document !== "undefined" && document.hidden)) return null;
    if (now < this.settleUntil) return null;
    const id = this.d.deviceId();
    const hasCompanion = s.location.deviceId === id;
    let plan = nextPrompt({
      state: s,
      now,
      presence: this.d.presence(),
      profile: this.d.profile(),
      hasCompanion,
      busy: this.d.busy(),
    });
    if (!plan && !hasCompanion && !this.d.busy() && presentsReminders(s, id, this.d.profile()))
      plan = reminderPrompt(s, now);
    // Forget reminders that are no longer due (bounded map).
    for (const rid of this.shownReminders.keys())
      if (!s.reminders.some((r) => r.id === rid && r.status === "due")) this.shownReminders.delete(rid);
    if (plan?.kind === "reminder" && plan.reminderId) {
      if (this.shownReminders.has(plan.reminderId)) return null;
      this.shownReminders.set(plan.reminderId, now);
      if (this.shownReminders.size > MAX_TRACKED)
        this.shownReminders.delete(this.shownReminders.keys().next().value!);
    }
    if (!plan) return null;
    this.settleUntil = now + SETTLE_MS;
    this.d.present(plan);
    this.d.act({
      type: "prompt_shown",
      kind: plan.kind,
      nudgeType: plan.nudgeType,
      questId: plan.questId,
      reminderId: plan.reminderId,
      text: plan.text,
    });
    if (this.ignoreTimer) clearTimeout(this.ignoreTimer);
    this.ignoreTimer = null;
    if (plan.kind === "nudge")
      this.ignoreTimer = setTimeout(() => {
        this.ignoreTimer = null;
        this.d.act({ type: "prompt_outcome", outcome: "ignored" });
      }, IGNORE_AFTER);
    return plan;
  }
}
